import React, { useEffect, useRef, useState } from 'react';
import { select } from 'd3-selection';
import { scaleBand, scaleLinear } from 'd3-scale';
import { stack } from 'd3-shape';
import Styles from './ChanceOfScoringGraph.module.scss';

interface Props {
  data: DataProp[];
  mobile: boolean;
}

interface DataProp {
  month: string;
  fun: number;
  funColor: string;
  poor: number;
  poorColor: string;
  pumping: number;
  pumpingColor: string;
  rideable: number;
  rideableColor: string;
}

const keys = ['pumping', 'fun', 'rideable', 'poor'] as const;
const legend = [
  { key: 'pumping', label: 'Pumping', color: 'rgba(49, 78, 230, 1)' },
  { key: 'fun', label: 'Fun', color: 'rgba(49, 78, 230, .7)' },
  { key: 'rideable', label: 'Rideable', color: 'rgba(49, 78, 230, .3)' },
  { key: 'poor', label: 'Poor', color: 'rgba(228, 231, 237, 1)' },
];

const ChanceOfScoringGraph: React.FC<Props> = ({ data, mobile }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const height = 330;
  const margin = { top: mobile ? 0 : 60, right: 80, bottom: 40, left: 0 };
  const chartWidth = dimensions.width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Set up ResizeObserver to watch container size changes
  useEffect(() => {
    if (!containerRef.current) return undefined;

    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height,
        });
      }
    };

    // Initial measurement
    updateDimensions();

    // Set up ResizeObserver for dynamic resizing
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [height]);

  useEffect(() => {
    if (!svgRef.current || !data.length || dimensions.width === 0) return;
    select(svgRef.current).selectAll('*').remove();

    const svg = select(svgRef.current);
    const chartGroup = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Scales
    const xScale = scaleBand()
      .domain(data.map((d) => d.month))
      .range([0, chartWidth])
      .padding(0.4);

    const yScale = scaleLinear().domain([0, 100]).range([chartHeight, 0]);

    // Stack data
    const stackGenerator = stack<DataProp>().keys(keys as any);
    const stackedData = stackGenerator(data);

    // Draw bars
    stackedData.forEach((stackData, stackIndex) => {
      chartGroup
        .selectAll(`.bar-${keys[stackIndex]}`)
        .data(stackData as any)
        .enter()
        .append('rect')
        .attr('class', `bar-${keys[stackIndex]}`)
        .attr('x', (d: any) => xScale(d.data.month)!)
        .attr('y', (d: any) => yScale(d[1]))
        .attr('width', xScale.bandwidth())
        .attr('height', (d: any) => yScale(d[0]) - yScale(d[1]))
        .attr('rx', 3)
        .attr('ry', 3)
        .attr('fill', (d: any) => {
          const colorKey = `${keys[stackIndex]}Color`;
          return d.data[colorKey];
        });
    });

    // Create hover areas for each month
    const monthGroups = chartGroup
      .selectAll('.month-group')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'month-group');

    // Add invisible hover rectangles for each month
    monthGroups
      .append('rect')
      .attr('class', 'hover-area')
      .attr('x', (d) => xScale(d.month)!)
      .attr('y', 0)
      .attr('width', xScale.bandwidth())
      .attr('height', chartHeight)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer');

    // Create text labels for each month (initially hidden)
    const textGroups = monthGroups.append('g').attr('class', 'text-group').style('opacity', 0);

    // Add text labels positioned to the right of bars
    stackedData.forEach((stackData, stackIndex) => {
      textGroups
        .append('text')
        .attr('class', `label-${keys[stackIndex]}`)
        .attr('x', (d) => xScale(d.month)! + xScale.bandwidth() + 8) // 8px to the right of bar
        .attr('y', (d) => {
          const monthData = stackData.find((s: any) => s.data.month === d.month);
          if (!monthData) return 0;
          return yScale(monthData[1]) + (yScale(monthData[0]) - yScale(monthData[1])) / 2;
        })
        .attr('text-anchor', 'start')
        .attr('alignment-baseline', 'middle')
        .style('font-size', '14px')
        .style('font-weight', 600)
        .style('fill', '#222')
        .text((d) => {
          const monthData = stackData.find((s: any) => s.data.month === d.month);
          if (!monthData) return '';
          const value = monthData[1] - monthData[0];
          return value > 1 ? `${Math.round(value)}%` : '';
        });
    });

    // Add hover events
    monthGroups
      .on('mouseenter', function () {
        select(this).select('.text-group').style('opacity', 1);
      })
      .on('mouseleave', function () {
        select(this).select('.text-group').style('opacity', 0);
      });

    // Draw month labels
    chartGroup
      .selectAll('.month-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'month-label')
      .attr('x', (d) => xScale(d.month)! + xScale.bandwidth() / 2)
      .attr('y', -16)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'alphabetic')
      .style('fill', '#98A2AF')
      .style('font-size', '14px')
      .style('font-weight', 600)
      .text((d) => d.month);
  }, [chartHeight, chartWidth, data, margin.left, margin.top, mobile, dimensions.width]);

  // Don't render until we have dimensions
  if (dimensions.width === 0) {
    return <div ref={containerRef} className={Styles.chanceOfScoringGraph} />;
  }

  // Render SVG and legend
  return (
    <div ref={containerRef} className={Styles.chanceOfScoringGraph}>
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={height}
        role="img"
        focusable="false"
        aria-label="Chance of scoring graph"
      >
        <rect width={dimensions.width} height={height} fill="transparent" />
      </svg>
      <div style={{ display: 'flex', gap: 32, marginTop: 8, marginLeft: 24 }}>
        {legend.map((item) => (
          <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                display: 'inline-block',
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: item.color,
              }}
            />
            <span style={{ color: '#222', fontWeight: 600 }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChanceOfScoringGraph;
